/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  // Capture before bundle handlers so no purchase path can use a stale variant.
  const purchaseControls = '[name="add"], [data-sticky-atc-add], [data-rgbo-add], [data-fbt-add], .shopify-payment-button';
  const purchaseSection = (control) => {
    const bundle = control.closest('.fbt');
    if (bundle) {
      const picker = Array.from(document.querySelectorAll('variant-picker'))
        .find((el) => !el.closest('.js-quickbuy, .cc-featured-product')
          && el.dataset.url?.endsWith(`/${bundle.dataset.anchorHandle}`));
      return picker?.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
    }
    return control.closest('sticky-atc-panel')?.productSection
      || control.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
  };
  const blockUnresolvedPurchase = (evt) => {
    const control = evt.type === 'submit' ? evt.target : evt.target.closest?.(purchaseControls);
    if (!control) return;
    const section = purchaseSection(control);
    if (!section?.dataset.variantUpdating && !section?.dataset.variantUpdateFailed) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
  };
  window.addEventListener('click', blockUnresolvedPurchase, true);
  window.addEventListener('submit', blockUnresolvedPurchase, true);

  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /** Keep purchase controls blocked until the newest option request resolves. */
    setVariantUpdateState(updating, failed = false) {
      const section = this.section;
      if (updating) section.dataset.variantUpdating = 'true';
      else delete section.dataset.variantUpdating;
      if (failed) section.dataset.variantUpdateFailed = 'true';
      else delete section.dataset.variantUpdateFailed;
      const blocked = updating || failed;
      const form = this.getProductForm();
      if (form) {
        if (updating) form.setAttribute('aria-busy', 'true');
        else form.removeAttribute('aria-busy');
      }
      const controls = Array.from(section.querySelectorAll(purchaseControls));
      document.querySelectorAll('.fbt [data-fbt-add]').forEach((control) => {
        if (purchaseSection(control) === section) controls.push(control);
      });
      controls.forEach((control) => {
        if (blocked) {
          if (!control.hasAttribute('data-variant-saved-disabled')) {
            control.dataset.variantSavedDisabled = control.getAttribute('aria-disabled') ?? '';
            control.dataset.variantSavedInert = control.inert ? 'true' : 'false';
          }
          control.setAttribute('aria-disabled', 'true');
          if (control.matches('.shopify-payment-button')) control.inert = true;
        } else if (control.hasAttribute('data-variant-saved-disabled')) {
          const previous = control.dataset.variantSavedDisabled;
          if (previous) control.setAttribute('aria-disabled', previous);
          else control.removeAttribute('aria-disabled');
          control.inert = control.dataset.variantSavedInert === 'true';
          delete control.dataset.variantSavedDisabled;
          delete control.dataset.variantSavedInert;
        }
      });
      let status = section.querySelector('[data-variant-update-status]');
      if (!status) {
        status = document.createElement('p');
        status.setAttribute('data-variant-update-status', '');
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        section.appendChild(status);
      }
      status.className = failed ? 'alert bg-error-bg text-error-text' : 'visually-hidden';
      status.textContent = updating ? this.dataset.loadingText : failed ? this.dataset.updateErrorText : '';
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (window.__rgPruneAuto) window.__rgPruneAuto = false;
      else window.__rgPruneNotice = null;

      /* Revgear: a DELIBERATE pick of a sold-out option is allowed to stand.
         Prune exists so a colour change never strands the shopper on a size
         that colour does not have — but it also meant clicking a sold-out size
         did nothing at all: the picker bounced straight back to an available
         one, so Klaviyo's "Notify me when back in stock" button (which only
         renders when the SELECTED variant is unavailable) could never appear
         for that size. Letting the pick stand turns a click that did nothing
         into the one useful action, and adds no permanent UI to an already
         busy PDP (Jason, 2026-09-03).

         One-shot and window-scoped because updateContent() replaces this whole
         element before updateAvailability() runs, so an instance property would
         not survive the trip. Any later change re-arms normal prune. */
      const changed = evt.target;
      const pickedUnavailable = !!(changed && changed.classList
        && changed.classList.contains('is-unavailable'));
      /* Remember WHICH option was asked for rather than a bare boolean.
         updateAvailability() runs more than once per change (the replaced
         element re-initialises), so a flag consumed on the first call was gone
         by the time prune actually ran. Matching on the value is idempotent:
         it survives every re-render, and stops mattering by itself as soon as
         the shopper selects something available, because prune only fires on a
         selected UNAVAILABLE option. */
      window.__rgKeepUnavailable = pickedUnavailable
        ? String(changed.value || changed.dataset.value || '')
        : null;
      if (pickedUnavailable) window.__rgPruneNotice = null;
      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      const currentRequestId = (Number(this.section.dataset.variantRequestId) || 0) + 1;
      this.section.dataset.variantRequestId = String(currentRequestId);
      this.setVariantUpdateState(true);

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (String(currentRequestId) !== this.section.dataset.variantRequestId) return;

          const responseData = JSON.parse(responseText);
          if (!responseData || !Object.keys(responseData).length) throw new Error('Variant content is empty');

          // Update content
          let productContentUpdated = false;
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            if (!target || !newContent) throw new Error('Variant content is missing');
            this.updateContent(target, newContent, combinedProductUrl);
            if (target === this.section) productContentUpdated = true;
          });

          if (!productContentUpdated) throw new Error('Product section is missing');

          // A replacement may start another request (availability pruning).
          if (String(currentRequestId) !== this.section.dataset.variantRequestId) return;

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => {
            if (String(currentRequestId) === this.section.dataset.variantRequestId) {
              try {
                if (!newVariantPicker) throw new Error('Variant picker is missing');
                newVariantPicker.announceChange();
                if (String(currentRequestId) === this.section.dataset.variantRequestId) {
                  this.setVariantUpdateState(false);
                }
              } catch {
                if (String(currentRequestId) === this.section.dataset.variantRequestId) {
                  this.setVariantUpdateState(false, true);
                }
              }
            }
          }, 10);
        })
        .catch(() => {
          if (String(currentRequestId) !== this.section.dataset.variantRequestId) return;
          this.setVariantUpdateState(false, true);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /** Preserve listed-option nodes so screen readers retain their reading position. */
    updateListedPicker(toReplace, replaceWith) {
      const picker = toReplace.querySelector('variant-picker');
      const nextPicker = replaceWith.querySelector('variant-picker');
      if (!picker || !nextPicker || picker.dataset.url !== nextPicker.dataset.url) return false;
      const selectors = Array.from(picker.optionSelectors);
      const nextSelectors = nextPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');
      if (!selectors.length || selectors.length !== nextSelectors.length
          || selectors.some((selector) => selector.dataset.selectorType !== 'listed')
          || Array.from(nextSelectors).some((selector) => selector.dataset.selectorType !== 'listed')) return false;

      const inputs = Array.from(picker.querySelectorAll('input.js-option[type="radio"]'));
      const nextInputs = Array.from(nextPicker.querySelectorAll('input.js-option[type="radio"]'));
      const data = picker.querySelector('[type="application/json"]');
      const nextData = nextPicker.querySelector('[type="application/json"]');
      if (!data || !nextData || !inputs.length || inputs.length !== nextInputs.length
          || inputs.some((input, index) => input.id !== nextInputs[index].id
            || input.dataset.valueId !== nextInputs[index].dataset.valueId
            || !picker.querySelector(`label[for="${CSS.escape(input.id)}"]`)
            || !nextPicker.querySelector(`label[for="${CSS.escape(input.id)}"]`))) return false;

      const nextVariant = JSON.parse(nextData.textContent);
      const syncAttributes = (element, nextElement) => {
        Array.from(element.attributes).forEach((attr) => {
          if (!nextElement.hasAttribute(attr.name)) element.removeAttribute(attr.name);
        });
        Array.from(nextElement.attributes).forEach((attr) => {
          if (element.getAttribute(attr.name) !== attr.value) element.setAttribute(attr.name, attr.value);
        });
      };
      inputs.forEach((input, index) => {
        const nextInput = nextInputs[index];
        syncAttributes(input, nextInput);
        input.checked = nextInput.checked;
        const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const nextLabel = nextPicker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        clearTimeout(label.dataset.preloadTimeout);
        syncAttributes(label, nextLabel);
        if (label.innerHTML !== nextLabel.innerHTML) label.innerHTML = nextLabel.innerHTML;
      });
      picker.className = nextPicker.className;
      picker.productAvailable = nextPicker.classList.contains('variant-picker--product-available');
      data.textContent = nextData.textContent;
      picker.variant = nextVariant;
      setTimeout(() => picker.updateVariantContent(), 10);
      return true;
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Keep same-product listed controls attached; structural/product changes still replace.
        if (!productChange && this.updateListedPicker(toReplace, replaceWith)) return;

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      // Revgear: a notice explains an automatic option switch. The theme swaps
      // the whole picker for fetched markup after every change, so the notice
      // lives in a page-level slot and is re-rendered into whichever picker is
      // current; a shopper-initiated change clears it (see handleVariantChange).
      this.querySelectorAll('.option-selector__notice').forEach((n) => n.remove());
      const pending = window.__rgPruneNotice;
      if (pending && pending.section === this.section.id) {
        const host = this.querySelector(`.option-selector[data-option="${CSS.escape(pending.option)}"]`);
        if (host) {
          const note = document.createElement('p');
          note.className = 'option-selector__notice';
          note.setAttribute('role', 'status');
          note.textContent = pending.text;
          host.appendChild(note);
        }
      }
      /* A LINK to a sold-out variant is a deliberate request too. Prune was
         written for a colour change that strands a size, but it also fired on
         page load when ?variant= named a sold-out one: the shopper followed a
         link to White, landed on Black, and "Notify me when back in stock"
         never appeared because Klaviyo only renders it for the SELECTED
         variant (Jason, 2026-09-03 — this is what he actually hit).

         Once per page load, and only when the URL asked for that exact variant:
         a colour change afterwards prunes normally. Without a ?variant= param
         Shopify already selects an available variant, so nothing else changes. */
      if (!window.__rgUrlVariantChecked) {
        window.__rgUrlVariantChecked = true;
        try {
          var wanted = new URLSearchParams(window.location.search).get('variant');
          if (wanted) {
            var picked = this.querySelector('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
            var form = this.section && this.section.querySelector('[name="id"]');
            if (picked && form && String(form.value) === String(wanted)) {
              window.__rgPruneNotice = null;
              this.querySelectorAll('.option-selector__notice').forEach(function (n) { n.remove(); });
              return;
            }
          }
        } catch (e) { /* a malformed URL must never break the picker */ }
      }
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          /* The shopper asked for THIS sold-out option on purpose — leave it
             selected so the buy box can offer back-in-stock for it. Any other
             unavailable option still prunes, so a colour change that strands a
             different axis is corrected as before. */
          const keep = window.__rgKeepUnavailable;
          if (keep && String(selected.value || selected.dataset.value || '') === keep) return;
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            const selector = selected.closest('.option-selector');
            const from = selected.value || selected.dataset.value || '';
            const to = toSelect.value || toSelect.dataset.value || '';
            const others = Array.from(this.querySelectorAll('.option-selector'))
              .filter((s) => s !== selector)
              .map((s) => { const o = s.querySelector('.js-option:is(:checked, [aria-selected="true"])'); return o ? (o.value || o.dataset.value) : ''; })
              .filter(Boolean).join(' / ');
            window.__rgPruneNotice = {
              section: this.section.id,
              option: selector.dataset.option || '',
              text: `${from} is sold out${others ? ` in ${others}` : ''} \u2014 showing ${to}`
            };
            window.__rgPruneAuto = true;   // the click below is ours, not the shopper's
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');
      this.clearBisProxy();

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out: show the back-in-stock call to action at once instead of "Sold out" for the
      // ~250 ms it takes Klaviyo to inject its own button (which stays hidden; clicks are forwarded).
      if (!this.variant.available) {
        this.setBisProxy();
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Turns the add button into a "Notify me when back in stock" proxy for Klaviyo's
     * back-in-stock button. Falls back to a disabled "Sold out" if Klaviyo never injects.
     */
    setBisProxy() {
      const btn = this.addBtn;
      btn.disabled = false;
      btn.dataset.bisProxy = 'true';
      btn.textContent = theme.strings.notifyMe || theme.strings.noStock;
      // Klaviyo's back-in-stock script can take several seconds on a cold load. Fall back to a
      // disabled "Sold out" only if its button has not appeared after 15 s (measured on live:
      // 7–14 s on a cold load), and if it appears
      // later anyway (watched below), switch back to the notify state.
      this.bisTimer = setTimeout(() => {
        if (btn.dataset.bisClickPending === 'true') return;   // shopper already clicked; product-form is waiting for Klaviyo
        if (btn.dataset.bisProxy === 'true' && !document.querySelector('button[data-a11y-identifier^="bis-button"]')) {
          delete btn.dataset.bisProxy;
          btn.disabled = true;
          btn.textContent = theme.strings.noStock;
        }
      }, 15000);
      this.watchForBisButton();
    }

    /**
     * Re-applies the notify state when Klaviyo injects its button after the fallback fired.
     */
    watchForBisButton() {
      if (this.bisObserver) return;
      this.bisObserver = new MutationObserver(() => {
        if (!document.querySelector('button[data-a11y-identifier^="bis-button"]')) return;
        this.bisObserver.disconnect();
        this.bisObserver = null;
        clearTimeout(this.bisTimer);
        if (this.variant && !this.variant.available && this.addBtn && this.addBtn.dataset.bisProxy !== 'true') {
          this.addBtn.disabled = false;
          this.addBtn.dataset.bisProxy = 'true';
          this.addBtn.textContent = theme.strings.notifyMe || theme.strings.noStock;
        }
      });
      this.bisObserver.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * Removes the back-in-stock proxy state before the button is set for a new variant.
     */
    clearBisProxy() {
      clearTimeout(this.bisTimer);
      if (this.bisObserver) { this.bisObserver.disconnect(); this.bisObserver = null; }
      if (this.addBtn && this.addBtn.dataset.bisProxy) delete this.addBtn.dataset.bisProxy;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
